import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { RegApi } from "../api/services";
import { errorMessage } from "../api/client";
import { ROLE_HOME, useAuth } from "../auth/AuthProvider";
import { Field, PasswordInput } from "../components/common";
import { AuthLayout, ERR_CLS } from "./AuthPages";

const STEPS = ["Username & password", "Email code", "Finish"];

export default function RegisterWizard() {
  const { user, acceptSession } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ username: "", password: "", confirm: "", email: "" });
  const [regId, setRegId] = useState("");
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user && step < 2) return <Navigate to={ROLE_HOME[user.role]} replace />;

  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const need = (cond: boolean, msg: string) => { if (!cond) throw new Error(msg); };
  const run = async (fn: () => Promise<void>) => {
    setErr(""); setBusy(true);
    try { await fn(); } catch (ex: any) { setErr(ex.message && !ex.response ? ex.message : errorMessage(ex)); } finally { setBusy(false); }
  };
  const sendCode = () => run(async () => {
    need(f.username.trim().length >= 3, "Enter a username (at least 3 characters)");
    need(f.password.length >= 8, "Password must be at least 8 characters");
    need(f.password === f.confirm, "Passwords do not match");
    need(/^\S+@\S+\.\S+$/.test(f.email.trim()), "Enter a valid email address");
    const r = await RegApi.sendOtp({ username: f.username, password: f.password, email: f.email });
    setRegId(r.registration_id); setStep(1);
  });
  const verify = () => run(async () => {
    await acceptSession(await RegApi.verify(regId, otp));
    setStep(2);
  });

  const body = [
    <div className="space-y-4" key="0">
      <Field label="Username" hint="3-30 characters: letters, numbers, dot or underscore"><input className="fp-input" autoCapitalize="none" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value.toLowerCase() })} data-testid="wizard-username-input" /></Field>
      <Field label="Password" hint="At least 8 characters"><PasswordInput autoComplete="new-password" value={f.password} onChange={set("password")} data-testid="wizard-password-input" /></Field>
      <Field label="Confirm password"><PasswordInput autoComplete="new-password" value={f.confirm} onChange={set("confirm")} data-testid="wizard-confirm-input" /></Field>
      <Field label="Email address" hint="We'll send a 6-digit code here"><input className="fp-input" type="email" autoComplete="email" value={f.email} onChange={set("email")} data-testid="wizard-email-input" /></Field>
    </div>,
    <div className="space-y-4" key="1">
      <p className="text-sm text-[#036248]">Enter the 6-digit code we sent to <strong>{f.email}</strong>.</p>
      <Field label="Verification code"><input className="fp-input font-mono tracking-[0.4em]" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid="wizard-otp-input" /></Field>
      <button type="button" className="text-sm font-semibold text-[#036248] underline" onClick={() => { setStep(0); setOtp(""); }} data-testid="wizard-change-email">Change email / resend code</button>
    </div>,
    <div className="space-y-4 text-center" key="2" data-testid="wizard-success">
      <CheckCircle2 size={56} className="mx-auto text-[#036248]" />
      <p className="text-xl font-bold text-[#003F32]">Account successfully created</p>
      <p className="text-sm text-[#036248]">Your username is <strong>{f.username}</strong>. Activate your membership anytime by scanning your package QR and card serial.</p>
      <button className="fp-btn h-12 w-full bg-[#FFC400] text-[#003F32]" onClick={() => nav("/patient/verify", { replace: true })} data-testid="wizard-verify-button">Verify my account now</button>
      <button className="fp-btn-secondary h-12 w-full" onClick={() => nav("/patient", { replace: true })} data-testid="wizard-finish-button">Later — go to my dashboard</button>
    </div>,
  ];

  return (
    <AuthLayout title="Get FastPass" subtitle={`Step ${step + 1} of ${STEPS.length} · ${STEPS[step]}`}>
      <ol className="mb-6 flex gap-1" aria-label="Progress" data-testid="wizard-progress">
        {STEPS.map((s, i) => <li key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#036248]" : "bg-[#036248]/15"}`} />)}
      </ol>
      {body[step]}
      {err && <p role="alert" className={`${ERR_CLS} mt-4`} data-testid="wizard-error">{err}</p>}
      {step < 2 && (
        <button type="button" className="fp-btn mt-6 h-12 w-full bg-[#FFC400] text-[#003F32] hover:bg-[#F4DF8C]" disabled={busy || (step === 1 && otp.length !== 6)}
          onClick={step === 0 ? sendCode : verify} data-testid="wizard-next-button">
          {busy && <Loader2 size={16} className="animate-spin" />} {step === 0 ? "Send email code" : "Verify & create account"}
        </button>
      )}
      {step === 0 && <p className="mt-5 text-sm text-[#003F32]">Already registered? <Link to="/" className="font-semibold underline">Sign in</Link></p>}
    </AuthLayout>
  );
}
